import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Loader2, Trash2 } from 'lucide-react';

export const Notifications = () => {
  const { api } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  const fetchAll = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    try {
      const res = await api.get('/notifications');
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(t('success.notifications.loadError'));
    } finally {
      setLoading(false);
    }
  }, [api, t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const markRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      fetchAll();
    } catch (e) {
      toast.error(t('success.notifications.markReadError'));
    }
  };

  const markAll = async () => {
    try {
      await api.put('/notifications/read-all');
      toast.success(t('success.notifications.markedAllRead'));
      fetchAll();
    } catch (e) {
      toast.error(t('success.notifications.markAllError'));
    }
  };

  const deleteOne = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      toast.success(t('success.notifications.deleted'));
      setItems((prev) => prev.filter((n) => n.id !== id));
    } catch (e) {
      toast.error(t('success.notifications.deleteError'));
    }
  };

  return (
    <div className="p-6 h-full overflow-y-auto">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-semibold">{t('nav.notifications')}</h1>
          <p className="text-sm text-zinc-400">Panel mínimo de notificaciones in-app.</p>
        </div>
        <Button variant="outline" onClick={markAll}>Marcar todo leído</Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando...
        </div>
      ) : (
        <div className="bg-card border border-zinc-800 rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Título</TableHead>
                <TableHead>Mensaje</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-zinc-500">Sin notificaciones</TableCell>
                </TableRow>
              ) : (
                items.map(n => (
                  <TableRow key={n.id} className={n.read ? 'opacity-70' : ''}>
                    <TableCell>{n.createdAt || '-'}</TableCell>
                    <TableCell className="font-medium">{n.title}</TableCell>
                    <TableCell>{n.message}</TableCell>
                    <TableCell>{n.type}</TableCell>
                    <TableCell>{n.read ? 'Leída' : 'No leída'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {!n.read && (
                          <Button size="sm" variant="outline" onClick={() => markRead(n.id)}>Marcar leído</Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={() => deleteOne(n.id)}
                          aria-label="Eliminar notificación"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteOne(n.id)}
                          aria-label="Eliminar notificación"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
